import { useState, useEffect, useCallback } from 'react'
import { FileText, ChevronDown, Printer, RefreshCw, AlertTriangle } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import portariaService from '../../services/portariaService'
import leiloesService from '../../services/leiloesService'

function fmtDT(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function RelatorioSaida() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,   setLeiloes]   = useState([])
  const [leilaoId,  setLeilaoId]  = useState('')
  const [relatorio, setRelatorio] = useState(null)
  const [loading,   setLoading]   = useState(false)
  const [loadingL,  setLoadingL]  = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(console.error)
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId || !leilaoId) return
    setLoading(true)
    try {
      const data = await portariaService.relatorioSaida(orgId, leilaoId)
      setRelatorio(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [orgId, leilaoId])

  useEffect(() => { carregar() }, [carregar])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const dataEmissao = new Date().toLocaleDateString('pt-BR')

  return (
    <div className="space-y-4">

      {/* Título */}
      <div className="no-print flex items-center gap-2">
        <FileText className="w-5 h-5 text-orange-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relatório de Saída de Animais</h1>
      </div>

      {/* Seletor */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500 font-medium">Leilão</label>
            <div className="relative">
              <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                {loadingL
                  ? <option>Carregando...</option>
                  : leiloes.map(l => (
                      <option key={l.id} value={String(l.id)}>
                        {l.nome} — {new Date(l.data_leilao).toLocaleDateString('pt-BR')}
                      </option>
                    ))
                }
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={() => window.print()} disabled={!relatorio || loading}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium">
              <Printer size={15} /> Imprimir / PDF
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      {!leilaoId ? (
        <div className="no-print bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione um leilão
        </div>
      ) : loading ? (
        <div className="no-print bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
        </div>
      ) : !relatorio || relatorio.por_comprador.length === 0 ? (
        <div className="no-print bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhuma saída registrada para este leilão
        </div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm p-8">

          {/* Cabeçalho */}
          <div className="text-center mb-6 pb-4 border-b-2 border-gray-800">
            <h2 className="text-xl font-black uppercase tracking-wide text-gray-900">Relatório de Saída de Animais</h2>
            {leilaoAtual && (
              <p className="text-sm font-semibold text-gray-700 mt-1">
                {leilaoAtual.nome} — {new Date(leilaoAtual.data_leilao).toLocaleDateString('pt-BR')}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">Emitido em {dataEmissao}</p>
          </div>

          {/* Resumo geral */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total faturado', value: relatorio.total_faturado, color: 'bg-blue-50 border-blue-200 text-blue-800' },
              { label: 'Total embarcado', value: relatorio.total_embarcado, color: 'bg-green-50 border-green-200 text-green-800' },
              {
                label: 'Pendente embarque',
                value: relatorio.pendente_geral,
                color: relatorio.pendente_geral > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-800'
              },
            ].map(c => (
              <div key={c.label} className={`rounded-lg border px-4 py-3 text-center ${c.color}`}>
                <div className="text-2xl font-black">{c.value} <span className="text-base font-normal">cab</span></div>
                <div className="text-xs font-medium mt-0.5">{c.label}</div>
              </div>
            ))}
          </div>

          {/* Por comprador */}
          <div className="space-y-6">
            {relatorio.por_comprador.map(grupo => (
              <div key={grupo.comprador_id || grupo.nome} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between">
                  <span className="font-bold text-sm uppercase tracking-wide">{grupo.nome}</span>
                  <div className="flex items-center gap-4 text-xs text-gray-300">
                    <span>Faturado: <strong className="text-white">{grupo.faturado}</strong> cab</span>
                    <span>Embarcado: <strong className="text-white">{grupo.embarcado}</strong> cab</span>
                    {grupo.pendente > 0 && (
                      <span className="text-amber-300 font-semibold">Pendente: {grupo.pendente} cab</span>
                    )}
                    {grupo.pendente === 0 && (
                      <span className="text-green-300 font-semibold">✓ Completo</span>
                    )}
                  </div>
                </div>

                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-1.5 text-left text-gray-600 font-semibold">Fazenda destino</th>
                      <th className="border border-gray-200 px-3 py-1.5 text-center text-gray-600 font-semibold">Embarcado</th>
                      <th className="border border-gray-200 px-3 py-1.5 text-center text-gray-600 font-semibold">Machucado</th>
                      <th className="border border-gray-200 px-3 py-1.5 text-left text-gray-600 font-semibold">Motorista</th>
                      <th className="border border-gray-200 px-3 py-1.5 text-left text-gray-600 font-semibold">Placa</th>
                      <th className="border border-gray-200 px-3 py-1.5 text-left text-gray-600 font-semibold">Horário</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.saidas.map((s, idx) => (
                      <tr key={s.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-200 px-3 py-1.5 text-gray-800">{s.fazenda || '—'}</td>
                        <td className="border border-gray-200 px-3 py-1.5 text-center font-semibold text-gray-900">{s.qtd_embarcada}</td>
                        <td className="border border-gray-200 px-3 py-1.5 text-center">
                          {s.qtd_machucado > 0 ? (
                            <span className="flex items-center justify-center gap-1 text-amber-700">
                              <AlertTriangle size={10} /> {s.qtd_machucado}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="border border-gray-200 px-3 py-1.5 text-gray-700">{s.motorista || '—'}</td>
                        <td className="border border-gray-200 px-3 py-1.5 font-mono text-gray-700">{s.placa || '—'}</td>
                        <td className="border border-gray-200 px-3 py-1.5 text-gray-600">{fmtDT(s.data_hora_saida)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Total geral */}
          <div className="mt-6 pt-4 border-t-2 border-gray-800">
            <table className="w-full border-collapse text-sm">
              <tbody>
                <tr className="bg-gray-900 text-white">
                  <td className="px-4 py-2 font-bold uppercase text-xs text-center" colSpan={6}>TOTAL GERAL</td>
                </tr>
                <tr className="bg-gray-100 font-semibold text-gray-800">
                  <td className="border border-gray-300 px-4 py-2">{relatorio.por_comprador.length} comprador(es)</td>
                  <td className="border border-gray-300 px-4 py-2">{relatorio.total_faturado} cab faturados</td>
                  <td className="border border-gray-300 px-4 py-2">{relatorio.total_embarcado} cab embarcados</td>
                  <td className="border border-gray-300 px-4 py-2 font-bold" colSpan={3}>
                    {relatorio.pendente_geral > 0
                      ? `${relatorio.pendente_geral} cab pendentes de embarque`
                      : 'Todos embarcados'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          .print-page { border: none !important; box-shadow: none !important; border-radius: 0 !important; padding: 10mm !important; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #aaa; padding: 3px 8px; font-size: 10px; }
        }
      `}</style>
    </div>
  )
}
